import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeftRight,
  PackagePlus,
  PackageX,
  ReceiptText,
  ScanText,
  BarChart3,
  BookOpen,
  Boxes,
  ChefHat,
  ClipboardCheck,
  Factory,
  FileDown,
  FileText,
  Scale,
  FolderTree,
  LayoutDashboard,
  LineChart,
  Package,
  Settings,
  Sheet,
  Shirt,
  ShoppingCart,
  Truck,
  Users,
  UtensilsCrossed,
  Warehouse,
  WashingMachine,
} from 'lucide-react';
import type { UserRole } from '@/types/auth';

/** Подписи ролей пользователей */
export const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: 'Администратор',
  MANAGER: 'Менеджер',
  OPERATOR: 'Оператор',
  VIEWER: 'Наблюдатель',
};

/** Быстрое создание документов — кнопка «Создать» в шапке и палитра команд */
export const QUICK_ACTIONS: { label: string; path: string; icon: LucideIcon; hint: string }[] = [
  { label: 'Приход', path: '/documents/receipt', icon: PackagePlus, hint: 'Поступление от поставщика' },
  { label: 'Приход по скану', path: '/documents/receipt/scan', icon: ScanText, hint: 'Фото или PDF накладной' },
  { label: 'Списание', path: '/documents/writeoff', icon: PackageX, hint: 'Списание со склада' },
  { label: 'Перемещение', path: '/stock-movements/create', icon: ArrowLeftRight, hint: 'Между складами' },
  { label: 'Выпуск продукции', path: '/production/new', icon: Factory, hint: 'Производство по рецептурам' },
  { label: 'Инвентаризация', path: '/inventory/create', icon: ClipboardCheck, hint: 'Пересчёт остатков' },
  { label: 'Продажа', path: '/sales', icon: ShoppingCart, hint: 'Касса' },
  { label: 'Приём белья', path: '/laundry/new', icon: WashingMachine, hint: 'Бланк прачечной' },
];

export interface NavChild {
  label: string;
  /** Путь, может содержать query: /documents?type=RECEIPT */
  path: string;
}

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  roles?: UserRole[];
  children?: NavChild[];
  /** Доп. слова для поиска в палитре команд */
  keywords?: string;
}

export interface NavGroup {
  id: string;
  label?: string;
  items: NavItem[];
}

/**
 * Единая карта разделов: из неё строятся боковое меню, «хлебные крошки» и палитра команд (⌘K).
 * Порядок групп повторяет ежедневный поток работы: склад → производство → продажи → справочники → аналитика.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'overview',
    items: [
      { id: 'dashboard', label: 'Обзор', icon: LayoutDashboard, path: '/dashboard', keywords: 'дашборд главная сводка' },
    ],
  },
  {
    id: 'stock',
    label: 'Склад',
    items: [
      {
        id: 'documents',
        label: 'Документы',
        icon: FileText,
        path: '/documents',
        keywords: 'накладные приход списание перемещение корректировка',
        children: [
          { label: 'Все документы', path: '/documents' },
          { label: 'Приходы', path: '/documents?type=RECEIPT' },
          { label: 'Перемещения', path: '/documents?type=TRANSFER' },
          { label: 'Списания', path: '/documents?type=WRITEOFF' },
          { label: 'Корректировки', path: '/documents?type=INVENTORY_ADJUSTMENT' },
        ],
      },
      { id: 'journal', label: 'Журнал документов', icon: BookOpen, path: '/journal', keywords: 'журнал история операций' },
      { id: 'stock-balances', label: 'Остатки', icon: Boxes, path: '/stock-balances', keywords: 'остатки склад количество' },
      { id: 'stock-movements', label: 'Движения', icon: ArrowLeftRight, path: '/stock-movements', keywords: 'движения товаров перемещения' },
      { id: 'inventory', label: 'Инвентаризация', icon: ClipboardCheck, path: '/inventory', keywords: 'пересчёт ревизия' },
    ],
  },
  {
    id: 'production',
    label: 'Производство',
    items: [
      {
        id: 'production',
        label: 'Выпуск продукции',
        icon: Factory,
        path: '/production',
        keywords: 'выпуск производство кухня',
        children: [
          { label: 'Все выпуски', path: '/production' },
          { label: 'Со списанием', path: '/production?type=WRITEOFF' },
          { label: 'С реализацией', path: '/production?type=SALE' },
          { label: 'Розница', path: '/production?type=RETAIL' },
        ],
      },
      {
        id: 'meal-system',
        label: 'Данные о питании',
        icon: FileDown,
        path: '/meal-system',
        // Менеджер настраивает соответствия и грузит, оператору это не нужно
        roles: ['ADMIN', 'MANAGER'],
        keywords: 'dfs24 импорт интеграция питание питавшиеся завтрак обед ужин выгрузка расписание',
      },
      { id: 'recipes', label: 'Рецептуры', icon: ChefHat, path: '/recipes', keywords: 'техкарты технологические карты блюда' },
      {
        id: 'menu',
        label: 'Меню',
        icon: UtensilsCrossed,
        path: '/menu',
        keywords: 'меню блюда позиции',
        children: [
          { label: 'Все меню', path: '/menu' },
          { label: 'Позиции меню', path: '/menu/items' },
          { label: 'Категории меню', path: '/menu/categories' },
          { label: 'Меню по складам', path: '/menu/warehouse-settings' },
          { label: 'Доступность блюд', path: '/menu/display' },
        ],
      },
    ],
  },
  {
    id: 'sales',
    label: 'Продажи',
    items: [
      { id: 'sales', label: 'Касса', icon: ShoppingCart, path: '/sales', keywords: 'касса продажа чек pos' },
      { id: 'sales-history', label: 'История продаж', icon: ReceiptText, path: '/sales/history', keywords: 'история продаж чеки возврат' },
      { id: 'sales-dashboard', label: 'Аналитика продаж', icon: LineChart, path: '/sales/dashboard', keywords: 'продажи выручка' },
    ],
  },
  {
    id: 'laundry',
    label: 'Прачечная',
    items: [
      {
        id: 'laundry',
        label: 'Приём и выдача',
        icon: WashingMachine,
        path: '/laundry',
        keywords: 'прачечная стирка бельё спецодежда бланк приём выдача',
      },
      {
        id: 'laundry-statement',
        label: 'Ведомость стирки',
        icon: Sheet,
        path: '/laundry/statement',
        keywords: 'ведомость стирки счёт кг декада прачечная',
      },
      {
        id: 'laundry-settings',
        label: 'Прачечные и изделия',
        icon: Shirt,
        path: '/laundry/settings',
        keywords: 'прачечная справочник изделия нормы веса простыня куртка',
      },
    ],
  },
  {
    id: 'directories',
    label: 'Справочники',
    items: [
      { id: 'products', label: 'Номенклатура', icon: Package, path: '/products', keywords: 'товары продукты сырьё' },
      {
        id: 'receipt-rules',
        label: 'Правила прихода',
        icon: Scale,
        path: '/receipt-rules',
        // Правит справочник менеджер и выше, как и прочие справочники
        roles: ['ADMIN', 'MANAGER'],
        keywords: 'правила прихода яйцо штуки килограммы консервация банка зелень специи пересчёт вес штуки',
      },
      { id: 'categories', label: 'Категории', icon: FolderTree, path: '/categories', keywords: 'группы папки номенклатуры' },
      { id: 'warehouse', label: 'Склады', icon: Warehouse, path: '/warehouse', keywords: 'склады точки' },
      { id: 'suppliers', label: 'Поставщики', icon: Truck, path: '/suppliers', keywords: 'поставщики' },
      {
        id: 'counterparties',
        label: 'Контрагенты',
        icon: Users,
        path: '/counterparties',
        keywords: 'контрагенты покупатели',
        children: [
          { label: 'Справочник', path: '/counterparties' },
          { label: 'Договоры', path: '/contracts' },
        ],
      },
    ],
  },
  {
    id: 'analytics',
    label: 'Аналитика',
    items: [
      { id: 'reports', label: 'Отчёты', icon: BarChart3, path: '/reports', keywords: 'отчёты abc xyz оборот рентабельность' },
    ],
  },
  {
    id: 'admin',
    label: 'Администрирование',
    items: [
      {
        id: 'settings',
        label: 'Настройки',
        icon: Settings,
        path: '/settings',
        // Менеджер видит здесь только журнал аудита
        roles: ['ADMIN', 'MANAGER'],
        keywords: 'пользователи аудит журнал резервные копии предприятия распознавание сканов deepseek ключ',
      },
    ],
  },
];

/** Названия страниц, которых нет в меню, — для «хлебных крошек» */
const EXTRA_TITLES: { pattern: RegExp; title: string; parent?: string }[] = [
  { pattern: /^\/documents\/receipt$/, title: 'Новый приход', parent: '/documents' },
  { pattern: /^\/documents\/receipt\/scan$/, title: 'Приход по скану', parent: '/documents' },
  { pattern: /^\/documents\/writeoff$/, title: 'Новое списание', parent: '/documents' },
  { pattern: /^\/stock-movements\/create$/, title: 'Новое перемещение', parent: '/stock-movements' },
  { pattern: /^\/inventory\/create$/, title: 'Новая инвентаризация', parent: '/inventory' },
  { pattern: /^\/inventory\/[^/]+\/analysis$/, title: 'Анализ расхождений', parent: '/inventory' },
  { pattern: /^\/inventory\/[^/]+$/, title: 'Карточка инвентаризации', parent: '/inventory' },
  { pattern: /^\/production\/new$/, title: 'Новый выпуск', parent: '/production' },
  { pattern: /^\/production\/[^/]+\/edit$/, title: 'Редактирование выпуска', parent: '/production' },
  { pattern: /^\/production\/[^/]+$/, title: 'Карточка выпуска', parent: '/production' },
  { pattern: /^\/menu\/[^/]+\/edit$/, title: 'Редактирование меню', parent: '/menu' },
  { pattern: /^\/laundry\/new$/, title: 'Новый бланк', parent: '/laundry' },
  { pattern: /^\/laundry\/\d+\/edit$/, title: 'Редактирование бланка', parent: '/laundry' },
  { pattern: /^\/laundry\/\d+$/, title: 'Бланк', parent: '/laundry' },
  { pattern: /^\/contracts\/new$/, title: 'Новый договор', parent: '/contracts' },
  { pattern: /^\/contracts\/[^/]+\/edit$/, title: 'Редактирование договора', parent: '/contracts' },
  { pattern: /^\/contracts$/, title: 'Договоры', parent: '/counterparties' },
];

export interface Crumb {
  label: string;
  path?: string;
}

const findByPath = (pathname: string) => {
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (item.path === pathname) return { group, item };
      const child = item.children?.find((c) => c.path.split('?')[0] === pathname && c.path !== item.path);
      if (child) return { group, item, child };
    }
  }
  return null;
};

/** «Хлебные крошки» для текущего адреса: Раздел › Страница */
export const getBreadcrumbs = (pathname: string, search: string): Crumb[] => {
  const extra = EXTRA_TITLES.find((entry) => entry.pattern.test(pathname));
  if (extra) {
    const parentCrumbs = extra.parent ? getBreadcrumbs(extra.parent, '') : [];
    const parent = parentCrumbs[parentCrumbs.length - 1];
    return [
      ...parentCrumbs.slice(0, -1),
      ...(parent ? [{ ...parent, path: extra.parent }] : []),
      { label: extra.title },
    ];
  }

  const found = findByPath(pathname);
  if (!found) return [];
  const { group, item, child } = found;
  const crumbs: Crumb[] = [];
  if (group.label) crumbs.push({ label: group.label });

  // Подраздел по query (?type=RECEIPT) — «Документы › Приходы»
  const typeChild = search
    ? item.children?.find((c) => {
        const [childPath, query] = c.path.split('?');
        if (!query || childPath !== pathname) return false;
        const target = new URLSearchParams(query);
        const current = new URLSearchParams(search);
        return [...target.entries()].every(([key, value]) => current.get(key) === value);
      })
    : undefined;

  if (child) {
    crumbs.push({ label: item.label, path: item.path });
    crumbs.push({ label: child.label });
  } else if (typeChild) {
    crumbs.push({ label: item.label, path: item.path });
    crumbs.push({ label: typeChild.label });
  } else {
    crumbs.push({ label: item.label });
  }
  return crumbs;
};

/** Активен ли пункт меню для текущего адреса (учитывает query ?type=…) */
export const isPathActive = (path: string, pathname: string, search: string): boolean => {
  const [targetPath, query] = path.split('?');
  if (pathname !== targetPath) return false;
  const current = new URLSearchParams(search);
  if (!query) return !current.has('type');
  const target = new URLSearchParams(query);
  return [...target.entries()].every(([key, value]) => current.get(key) === value);
};

/**
 * Пункт меню, к которому относится текущий адрес (включая вложенные страницы /inventory/12).
 * Выбирается самый длинный подходящий путь: /sales/dashboard → «Аналитика продаж», а не «Касса».
 */
export const getActiveItemId = (pathname: string): string | null => {
  let best: { id: string; length: number } | null = null;
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      const paths = [item.path, ...(item.children?.map((c) => c.path.split('?')[0]) ?? [])];
      for (const p of paths) {
        if ((pathname === p || pathname.startsWith(`${p}/`)) && (!best || p.length > best.length)) {
          best = { id: item.id, length: p.length };
        }
      }
    }
  }
  return best?.id ?? null;
};

/** Все пункты меню плоским списком (для палитры команд) */
export const flatNavItems = (): (NavItem & { group?: string })[] =>
  NAV_GROUPS.flatMap((group) => group.items.map((item) => ({ ...item, group: group.label })));
